import React, { Suspense, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import Sidebar from '../Sidebar';
import AccessExpiryBanner from '../access/AccessExpiryBanner';
import { ToastProvider } from '../news/Toast';

// Lazy: mantém os ~40KB de markdown dos documentos legais fora do bundle inicial.
const LegalConsentGate = React.lazy(() => import('../legal/LegalConsentGate'));

export const RouteLoading: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="nn-loading" role="status">
      <i className="fas fa-spinner fa-spin" />
      <span>{t('common.loading')}</span>
    </div>
  );
};

// Layout NeuroNews v3: sidebar do protótipo + área principal.
export const AppShell: React.FC = () => {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, location.search]);

  return (
    <ToastProvider>
      <div className="nn-root">
        <button
          type="button"
          className="menu-btn"
          onClick={() => setMobileOpen((prev) => !prev)}
          aria-label={t('news.nav.menu')}
        >
          <svg viewBox="0 0 24 24">
            <path d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <div className="app">
          <Sidebar mobileOpen={mobileOpen} onNavigate={() => setMobileOpen(false)} />
          <main className="main">
            {/* Prazo de acesso (TEXTOS 1 e 2). Boundary próprio: uma falha
                aqui some com o aviso, não com a aplicação. */}
            <Sentry.ErrorBoundary
              fallback={<></>}
              beforeCapture={(scope) => scope.setTag('area', 'access-banner')}
            >
              <AccessExpiryBanner />
            </Sentry.ErrorBoundary>
            <Suspense fallback={<RouteLoading />}>
              <Outlet />
            </Suspense>
          </main>
        </div>
        {/* Irmão do .app, não gate antes do <Outlet />: o modal se sobrepõe à
            aplicação montada, e qualquer falha ao consultar o status resulta
            em nada renderizado — nunca em acesso bloqueado.
            O boundary próprio estende isso às falhas de RENDER e de carga do
            chunk: sem ele, o erro subiria até o boundary da raiz e trocaria a
            aplicação inteira pela tela de erro (no RadExperts, em 30/09/2026,
            foi o que aconteceu em navegadores antigos). */}
        <Sentry.ErrorBoundary
          fallback={<></>}
          beforeCapture={(scope) => scope.setTag('area', 'legal-consent')}
        >
          <Suspense fallback={null}>
            <LegalConsentGate />
          </Suspense>
        </Sentry.ErrorBoundary>
      </div>
    </ToastProvider>
  );
};

// Telas existentes (Tailwind) dentro do shell novo: só ganham o respiro lateral.
export const LegacyPage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="nn-legacy-page">{children}</div>
);
